import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import type {
  AspectRatio,
  MediaGeneratorKey,
  MediaGenerationView,
  MediaModel,
  ReferenceRoleId,
} from '@mg/shared';
import {
  ASPECT_RATIOS,
  MediaProvider,
  POST_PROMPT_STYLES,
  defaultModelFor,
  maxReferencesFor,
  modelsFor,
  randomSeed,
  ratiosFor,
} from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { FormField, Select, Textarea } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { AspectRatioSelect, ratioLabel } from './AspectRatioSelect';
import { GenerationPreview } from './GenerationPreview';
import { MediaLibraryOverlay } from './MediaLibraryOverlay';
import { MediaReferenceCard, type MediaReference } from './MediaReferenceCard';
import { useDesignTools } from './useDesignTools';
import { generatorChoices } from './generatorChoices';
import { useGeneratorPreferences } from './useGeneratorPreferences';
import { PostContextBand } from '../post-context/PostContextBand';
import { PromptFromPostRow } from '../post-context/PromptFromPostRow';
import type { PostContext, SaveToPost } from '../post-context/postContext';

/** Sentinel for the Canva/Figma entry — an IMPORT, so it navigates instead of setting provider. */
const DESIGN_TOOL_OPTION = '__design_tool__';

type GeneratorKey = 'azure' | MediaProvider;

/**
 * Generate Media — a two-column workspace rather than a fire-and-forget form.
 *
 * The left column is the request; the right is the result. The modal STAYS OPEN after generating so
 * a user can refine and compare versions. EVERY generated image is added to the Media Library
 * automatically, server-side, at the moment the provider finishes — there is no keep/discard step,
 * so closing the modal never loses an image and an async provider finishing after the modal has gone
 * still lands its result.
 *
 * Controls are driven by the model registry, not by hope. Providers do not report unsupported
 * parameters — Azure FLUX accepts an aspect ratio and silently returns a square — so anything the
 * chosen model can't do is disabled here with a reason, never sent and wished upon.
 */
export function GenerateMediaModal({
  open,
  onClose,
  folderId,
  tenantKey,
  onUseDesignTool,
  postContext,
  onSaveToPost,
}: {
  open: boolean;
  onClose: () => void;
  folderId?: string;
  tenantKey: string;
  onUseDesignTool?: () => void;
  /** The post this was opened from: shows it above the form, with prompt drafting from it. */
  postContext?: PostContext;
  /** Set by the post editor: "Save and return to post" puts the chosen image in the post. */
  onSaveToPost?: SaveToPost;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const designTools = useDesignTools();
  const { tool: designTool, label: designToolName } = designTools;
  // A module mock (13 composer specs) or an older hook may omit the field: that keeps today's behaviour.
  const builtInImageGeneration = designTools.builtInImageGeneration !== false;

  const [generator, setGenerator] = useState<GeneratorKey>('azure');
  const [modelId, setModelId] = useState<string>(defaultModelFor('azure')?.id ?? '');
  const [prompt, setPrompt] = useState('');
  const [refs, setRefs] = useState<MediaReference[]>([]);
  const [aspect, setAspect] = useState<AspectRatio>('1:1');
  const [megapixels, setMegapixels] = useState<number>(2);
  const [versions, setVersions] = useState<MediaGenerationView[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [changeRequest, setChangeRequest] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Set while a generation is in flight, so the modal refuses to close over paid work. */
  const [pendingId, setPendingId] = useState<string | null>(null);
  /** The library browser lives over the preview pane; the form column stays usable behind it. */
  const [libraryOpen, setLibraryOpen] = useState(false);
  /** Bumped to reach into the reference card's file input — the overlay has none of its own. */
  const [uploadSignal, setUploadSignal] = useState(0);
  /** Which named slot the library picker is filling, when the model uses roles. */
  const [libraryRole, setLibraryRole] = useState<ReferenceRoleId | undefined>(undefined);
  /** The provider's named size tier, for models that take one instead of pixels. */
  const [resolution, setResolution] = useState<string | undefined>(undefined);
  /**
   * The generator whose dependent settings have already been reconciled.
   *
   * Distinguishes "the user switched generator" from "we just restored a remembered one". Without it
   * the switch effect fires on every open and replaces the remembered model with the provider's
   * default, which looks exactly like the preference never being saved.
   */
  const reconciledFor = useRef<GeneratorKey | null>(null);

  const promptId = useFieldId('gen-prompt');
  const providerId = useFieldId('gen-provider');
  const modelFieldId = useFieldId('gen-model');
  const aspectId = useFieldId('gen-aspect');
  const mpId = useFieldId('gen-mp');
  const noticeId = useFieldId('gen-unavailable');

  const creds = useQuery({
    queryKey: ['media-credentials', tenantKey],
    queryFn: ({ signal }) => api.listMediaCredentials(signal),
    enabled: open,
  });
  /**
   * BYO providers that are actually usable — a configured key AND at least one registered model.
   *
   * The key alone used to be enough to list one, but the server validates the model against the
   * registry, so selecting a provider with no entry returned 400 before any provider was contacted.
   * Worse, `defaultModelFor` returning undefined left the PREVIOUS provider's model id in state, so
   * the error named a model belonging to a different generator. Listing only what can run is the
   * honest version; a provider gains its entry when its generator can actually send our parameters.
   */
  const byoProviders = useMemo(
    () =>
      Array.from(
        new Set((creds.data ?? []).filter((c) => c.hasKey).map((c) => c.provider)),
      ).filter((p) => modelsFor(p as MediaProvider).length > 0) as MediaProvider[],
    [creds.data],
  );

  /**
   * Generators the user could actually pick, in dropdown order — the set the stored preference is
   * validated against, so a remembered provider whose key has since been removed falls back rather
   * than restoring into a 400. The built-in FLUX is among them only while the install has image
   * generation switched on (App Settings → AI models): switched off, the server refuses it.
   */
  const availableGenerators = useMemo<MediaGeneratorKey[]>(
    () => generatorChoices(builtInImageGeneration, byoProviders),
    [builtInImageGeneration, byoProviders],
  );
  const { prefs, remember } = useGeneratorPreferences(availableGenerators);
  /**
   * The generator in state is one on offer. False while it isn't: the built-in FLUX with image
   * generation switched off, until the fallback below moves to a provider with a key, or for good
   * when there is none. Its models are then not offered, and nothing can be generated.
   */
  const generatorAvailable = availableGenerators.includes(generator);
  /**
   * Nothing to generate with: the built-in generator is switched off and no provider has a key. Only
   * once the keys are known (or couldn't be read), so the notice never flashes up while they load on an
   * install that has some. While it shows, it is what the Generator field and Generate are described
   * by: a screen reader reaching either hears why nothing can be generated, and who can fix it.
   */
  const showNotice =
    !builtInImageGeneration && byoProviders.length === 0 && (creds.isSuccess || creds.isError);

  // Memoised so the empty list is one value: `modelOptions` below depends on it.
  const models = useMemo(
    () => (generatorAvailable ? modelsFor(generator) : []),
    [generatorAvailable, generator],
  );
  const model: MediaModel | undefined = models.find((m) => m.id === modelId) ?? models[0];
  /**
   * Alphabetical FOR DISPLAY ONLY.
   *
   * Every label leads with its family — "Magnific · Seedream 5 Pro", "Magnific · FLUX.2 [klein]" —
   * so sorting by label groups the families without needing an explicit grouping key. `localeCompare`
   * rather than `<`, so "FLUX.2" and "Flux Dev" sit together instead of the uppercase ones being
   * hoisted above every lowercase one, and `numeric` so "Imagen 4" precedes "Imagen 10".
   *
   * NOT sorted in the registry itself: `defaultModelFor` takes the FIRST entry, so reordering there
   * would change which model is preselected — alphabetically that becomes FLUX Kontext, which
   * cannot run at all without a reference image.
   */
  const modelOptions = useMemo(
    () => [...models].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [models],
  );
  const allowedRatios = model ? ratiosFor(model) : [...ASPECT_RATIOS];
  const allowedMp = model?.caps.megapixels ?? [1, 2];
  const maxRefs = model ? maxReferencesFor(model.caps) : 0;
  const refRoles = model?.caps.references.kind === 'roles' ? model.caps.references.roles : undefined;
  /** Models whose endpoint refuses a request without references — the "(with references)" ones. */
  const minReferences =
    model?.caps.references.kind === 'list' ? (model.caps.references.min ?? 0) : 0;
  const resolutionTiers = model?.caps.resolutions;

  /**
   * Per open: restore the four remembered settings, clear everything else.
   *
   * The prompt and references are deliberately NOT restored — reopening to find last week's prompt
   * already in the box invites regenerating it by accident, and a remembered reference id can point
   * at an image that has since been deleted.
   *
   * `prefs` is read but intentionally omitted from the deps: this seeds ONCE per open. Reacting to
   * it would fight the user, since every change they make writes back into it.
   */
  useEffect(() => {
    if (!open) return;
    setGenerator(prefs.generator);
    setModelId(prefs.modelId);
    setAspect(prefs.aspect);
    setMegapixels(prefs.megapixels);
    // Tell the generator-change effect below that this generator is already settled, so it does not
    // immediately overwrite the restored model with that provider's default.
    reconciledFor.current = prefs.generator;

    setPrompt('');
    setRefs([]);
    setVersions([]);
    setActiveIndex(0);
    setChangeRequest('');
    setError(null);
    setPendingId(null);
    setLibraryOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Persist the four settings as they change, so the next open starts where this one left off. */
  useEffect(() => {
    if (!open) return;
    remember({ generator, modelId, aspect, megapixels });
  }, [open, generator, modelId, aspect, megapixels, remember]);

  // A model that takes no references has nothing to browse for — don't leave the picker stranded
  // over the preview after a generator switch.
  useEffect(() => {
    if (maxRefs === 0) setLibraryOpen(false);
  }, [maxRefs]);

  /**
   * References must fit the model that is now selected — a MODEL change, not just a generator one.
   *
   * The old trim ran only when the GENERATOR changed, so moving between two models of the same
   * provider carried them over untouched: picking four for FLUX.2 [pro] and then switching to
   * Mystic left the card reading "4 of 2 slots" with both slots showing Empty, because a list-style
   * reference has no role and a role-style card renders by role. Generating from that state would
   * have been refused by the API for unlabelled references — correct, but only after the fact.
   *
   * Crossing between list and roles drops them: the two shapes mean different things and there is no
   * honest mapping from "four interchangeable images" onto "one structure, one style".
   */
  useEffect(() => {
    if (!open) return;
    setRefs((prev) => {
      if (prev.length === 0) return prev;
      const caps = model?.caps.references;
      if (!caps || caps.kind === 'none') return [];
      if (caps.kind === 'roles') {
        const known = new Set(caps.roles.map((r) => r.id));
        const kept = prev.filter((r) => r.role && known.has(r.role));
        return kept.length === prev.length ? prev : kept;
      }
      // list: roles are meaningless here, and the cap may have shrunk.
      const kept = prev.slice(0, caps.max).map((r) => (r.role ? { ...r, role: undefined } : r));
      return kept.length === prev.length && kept.every((r, i) => r === prev[i]) ? prev : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, model?.id]);

  /**
   * Keep the resolution tier valid for the current model, and drop it entirely for models that
   * take pixels instead. Sending a tier a model doesn't recognise is silently ignored rather than
   * rejected, so the wrong one would just quietly produce a different size.
   */
  useEffect(() => {
    if (!resolutionTiers?.length) {
      setResolution(undefined);
      return;
    }
    setResolution((prev) =>
      prev && resolutionTiers.some((t) => t.value === prev) ? prev : resolutionTiers[0].value,
    );
  }, [resolutionTiers]);

  /**
   * A genuine generator CHANGE re-picks that provider's default model and drops any setting the new
   * model can't honour, rather than letting the provider silently ignore it.
   *
   * Guarded by `reconciledFor` so it does not fire for the generator the open-effect just restored —
   * without that, a remembered non-default model would be replaced by the default every open, and
   * the preference would look like it was never saved.
   */
  useEffect(() => {
    if (!open) return;
    if (reconciledFor.current === generator) return;
    reconciledFor.current = generator;
    const next = defaultModelFor(generator);
    if (!next) return;
    setModelId(next.id);
    if (!ratiosFor(next).includes(aspect)) setAspect(ratiosFor(next)[0] ?? '1:1');
    if (!next.caps.megapixels.includes(megapixels)) setMegapixels(next.caps.megapixels[0]);
    setRefs((prev) => prev.slice(0, maxReferencesFor(next.caps)));
    // aspect/megapixels intentionally omitted: this corrects them, it must not re-run on them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generator, open]);

  /**
   * A generator that isn't on offer falls back to the first one that is: a BYO provider whose key
   * disappears mid-session, or the built-in FLUX the dialog opened on (the preferences can only be
   * validated against the keys loaded by then) once the install has it switched off. With FLUX on,
   * the first on offer is FLUX itself, as before. With nothing on offer, nothing is chosen.
   */
  useEffect(() => {
    if (availableGenerators.length > 0 && !availableGenerators.includes(generator)) {
      setGenerator(availableGenerators[0]);
    }
  }, [availableGenerators, generator]);

  const active = versions[activeIndex] ?? null;

  /** Poll the in-flight generation; the grid no longer watches these for us. */
  const poll = useQuery({
    queryKey: ['media-generation', pendingId],
    queryFn: ({ signal }) => api.getGeneration(pendingId!, signal),
    enabled: pendingId != null,
    refetchInterval: (q) => (q.state.data?.status === 'GENERATING' ? 1500 : false),
  });

  useEffect(() => {
    const row = poll.data;
    if (!row || row.status === 'GENERATING') return;
    setPendingId(null);
    if (row.status === 'FAILED') {
      setError(row.error ?? 'The provider could not generate that image.');
      return;
    }
    setVersions((prev) => {
      const next = [...prev.filter((v) => v.id !== row.id), row];
      setActiveIndex(next.length - 1);
      return next;
    });
    setChangeRequest('');
    // The server already added it to the library, so the grid and the folder counts behind this
    // modal are now stale.
    void queryClient.invalidateQueries({ queryKey: ['media', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['asset-folders', tenantKey] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.data]);

  const start = useMutation({
    mutationFn: (body: Parameters<typeof api.startGeneration>[0]) => api.startGeneration(body),
    onSuccess: (row) => {
      setError(null);
      if (row.status === 'GENERATING') setPendingId(row.id);
      else {
        setVersions((prev) => {
          const next = [...prev, row];
          setActiveIndex(next.length - 1);
          return next;
        });
        setChangeRequest('');
        // Promotion happened server-side inside this request for a sync provider.
        void queryClient.invalidateQueries({ queryKey: ['media', tenantKey] });
        void queryClient.invalidateQueries({ queryKey: ['asset-folders', tenantKey] });
      }
    },
    onError: (err) => setError(actionErrorText(err, 'Could not start the generation.')),
  });

  /**
   * Work is in flight from the moment we ask for it, not from the moment we learn its id.
   *
   * `pendingId` is only set in `start.onSuccess`, and for the sync providers — the only selectable
   * ones — the POST *is* the generation. So a window covering the entire billed call left every
   * close affordance live, which is exactly what the lock exists to prevent. The Generate button
   * already accounted for `start.isPending`; the close path did not. Declared here rather than with
   * `active` above because it reads `start`, which does not exist yet up there.
   */
  const generating = pendingId != null || start.isPending;

  /**
   * Leave for the Media Library, where the generated image already is.
   *
   * Closes the modal on the way out — the host is driven by `?generate=media`, and navigating with
   * that param still set would reopen it on arrival.
   *
   * Carries the ASSET so the library can open it directly. Every finished generation is promoted
   * automatically, so `savedAssetId` is the row it became — landing on a grid of sixty tiles and
   * expecting someone to spot theirs is not "view in media library".
   *
   * Still no folder in the URL: MediaLibraryPage keeps folder selection in component state, so a
   * `?folderId=` would be silently ignored, and the All root lists every folder newest-first anyway.
   */
  const viewInLibrary = () => {
    const assetId = active?.savedAssetId;
    onClose();
    navigate(assetId ? `/media?asset=${encodeURIComponent(assetId)}` : '/media');
  };

  const submit = (opts?: { refine?: boolean; reseed?: boolean }) => {
    setError(null);
    // The picker covers the preview, which is where the result is about to appear — leaving it open
    // would hide the thing the click asked for.
    setLibraryOpen(false);
    start.mutate({
      folderId,
      generator,
      model: model?.id,
      prompt,
      aspectRatio: aspect,
      megapixels: megapixels as 1 | 2 | 4,
      referenceIds: refs.filter((r) => r.assetId).map((r) => r.assetId!),
      // Only for role-based models; a list-style model's references are interchangeable and
      // sending roles there would be noise the server has to ignore.
      ...(refRoles
        ? {
            referenceRoles: refs
              .filter((r) => r.assetId)
              .map((r) => r.role)
              .filter((r): r is ReferenceRoleId => Boolean(r)),
          }
        : {}),
      ...(resolution ? { resolution } : {}),
      ...(opts?.refine && active ? { parentId: active.id, changeRequest } : {}),
      // Drawn from the MODEL's own range: the bounds differ per provider and an out-of-range seed
      // is rejected outright (Magnific's flux-dev floor is 1, Seedream v4's ceiling is 2^31-1).
      ...(opts?.reseed && model?.caps.seed ? { seed: randomSeed(model.caps.seed) } : {}),
    });
  };

  /**
   * Closing is now lossless — every finished image is already in the library — so there is nothing
   * to confirm. The in-flight guard stays: the generation is billed either way, and abandoning it
   * mid-request means the user pays for something they never see appear.
   */
  const requestClose = () => {
    if (generating) return;
    onClose();
  };

  /**
   * "Save and return to post": the image on screen goes into the post, and the window closes.
   *
   * The image is already in the Media Library, so "save" means only that. Its asset is read back for
   * the small copy, so the post's tile shows the 112px thumbnail from the start.
   */
  const saveToPost = useMutation({
    mutationFn: (assetId: string) => api.getAsset(assetId),
    onSuccess: (asset) => {
      onSaveToPost?.({
        id: asset.id,
        blobUrl: asset.blobUrl,
        mimeType: asset.mimeType,
        posterUrl: asset.posterUrl,
        thumbUrl: asset.thumbUrl,
      });
      onClose();
    },
    onError: (err) => setError(actionErrorText(err, 'Could not add the image to your post.')),
  });

  /**
   * Also gated on the reference requirement, so a model that cannot run without one never gets a
   * click. The API refuses these anyway; letting the button fire only trades a clear inline notice
   * for an error in the preview pane after the fact. And on a generator that is on offer: with the
   * built-in FLUX switched off and no provider key, there is nothing to send the prompt to.
   */
  const canGenerate =
    prompt.trim().length > 0 &&
    !generating &&
    !start.isPending &&
    refs.filter((r) => r.assetId).length >= minReferences &&
    generatorAvailable;

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title="Generate media"
      size="5xl"
      closeDisabled={generating}
      closeDisabledReason="Wait for the image to finish — it's already been generated on your account."
      /**
       * Stacked (below md) the BODY scrolls and each section flows at its natural height. Two-pane
       * (md+) the body is a fixed box and each column scrolls independently.
       *
       * Stacked used to use the two-pane rules, which made the layout depend on flex-shrink
       * arithmetic: the form column had no basis of its own, so it was sized by its content and only
       * fitted because it happened to absorb all the shrink. Add a few reference thumbnails and it
       * stopped fitting — the inner scroller never got a bounded height, so it grew instead of
       * scrolling, `overflow-hidden` clipped the excess, and the Cancel/Generate bar was pushed
       * outside the panel. Letting the body scroll removes the contest entirely.
       */
      /**
       * Stacked (below md) the BODY scrolls. Two-pane (md+) each COLUMN scrolls inside its own
       * bounded height, so neither can push the other's action bar out of the panel.
       */
      bodyClassName="min-h-0 flex-1 overflow-y-auto md:flex md:flex-col md:overflow-hidden"
    >
      <div className="flex min-h-0 flex-col md:flex-1">
      {postContext && <PostContextBand context={postContext} title="Designing media for this post" />}
      <div className="flex min-h-0 flex-col md:flex-1 md:flex-row">
        {/* ── Left: the request ─────────────────────────────────────────── */}
        <div className="flex flex-col border-line-soft md:min-h-0 md:w-[23.75rem] md:shrink-0 md:border-r">
          {/*
              An explicit max-height, not just flex-1.

              `flex-1 min-h-0` only bounds this if every ancestor resolves a definite height, and
              one link in that chain going soft pushes the action bar below the panel edge instead
              of shrinking the scroller — which is exactly what four reference thumbnails did. 80vh
              is bounded outright: header + this + the bar stays inside the panel's 92vh. Less
              under a post's band, which takes its own share of that height.
            */}
            <div
              className={`space-y-4 px-5 py-4 md:min-h-0 md:flex-1 md:overflow-y-auto ${
                postContext ? 'md:max-h-[62vh]' : 'md:max-h-[74vh]'
              }`}
            >
            {showNotice && (
              <p id={noticeId} role="status" className="text-sm text-ink-muted">
                Image generation isn&apos;t available on this install yet. A Tenant Admin can switch on the
                built-in generator in App Settings → AI models, or add a provider key in Site
                Integrations → Media Generators.
              </p>
            )}
            <FormField label="Generator" htmlFor={providerId}>
              <Select
                id={providerId}
                value={generator}
                aria-describedby={showNotice ? noticeId : undefined}
                onChange={(e) => {
                  if (e.target.value === DESIGN_TOOL_OPTION) {
                    onUseDesignTool?.();
                    return;
                  }
                  setGenerator(e.target.value as GeneratorKey);
                }}
              >
                {builtInImageGeneration && <option value="azure">Smorgas Social &middot; FLUX</option>}
                {byoProviders.map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0) + p.slice(1).toLowerCase()}
                  </option>
                ))}
                {designTool && onUseDesignTool && (
                  <option value={DESIGN_TOOL_OPTION}>{designToolName} &middot; import a design</option>
                )}
              </Select>
            </FormField>

            <FormField label="Model" htmlFor={modelFieldId} hint={model?.hint}>
              <Select
                id={modelFieldId}
                value={model?.id ?? ''}
                onChange={(e) => setModelId(e.target.value)}
                disabled={models.length <= 1}
              >
                {modelOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </FormField>

            {/* Directly above the field it fills. */}
            {postContext && (
              <PromptFromPostRow
                context={postContext}
                kind="image"
                styles={POST_PROMPT_STYLES.image}
                label="Generate image prompt from your post"
                onDrafted={setPrompt}
              />
            )}

            <FormField label="Prompt" htmlFor={promptId}>
              <Textarea
                id={promptId}
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  postContext
                    ? 'Describe the image you want, or draft one from your post.'
                    : 'Describe the image you want…'
                }
              />
            </FormField>

            <MediaReferenceCard
              refs={refs}
              max={maxRefs}
              onChange={setRefs}
              disabledReason={
                maxRefs === 0 ? `${model?.label ?? 'This model'} doesn't accept reference images.` : undefined
              }
              minRequired={minReferences}
              onBrowseLibrary={(role) => {
                setLibraryRole(role);
                setLibraryOpen(true);
              }}
              uploadSignal={uploadSignal}
              roles={refRoles}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Aspect ratio" htmlFor={aspectId}>
                <AspectRatioSelect
                  id={aspectId}
                  value={aspect}
                  onChange={setAspect}
                  // The list would otherwise open UNDER the overlay's opaque backdrop where it
                  // overlaps the preview column, so the two surfaces take turns.
                  onOpen={() => setLibraryOpen(false)}
                  options={ASPECT_RATIOS.map((r) => ({
                    ratio: r,
                    enabled: allowedRatios.includes(r),
                    reason: `${model?.label ?? 'This model'} can't produce ${ratioLabel(r)}.`,
                  }))}
                />
              </FormField>
              {/*
                Two different controls behind one label. A model with named tiers takes no
                width/height at all, so showing megapixels there would be a number we compute and
                then discard — and the tier is the only size control it actually has.
              */}
              {resolutionTiers?.length ? (
                <FormField
                  label="Resolution"
                  htmlFor={mpId}
                  hint={`${model?.label ?? 'This model'} sizes by tier, not exact pixels.`}
                >
                  <Select
                    id={mpId}
                    value={resolution ?? resolutionTiers[0].value}
                    onChange={(e) => setResolution(e.target.value)}
                  >
                    {resolutionTiers.map((tier) => (
                      <option key={tier.value} value={tier.value}>
                        {tier.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
              ) : (
                <FormField
                  label="Resolution"
                  htmlFor={mpId}
                  hint={`≈${Math.round(Math.sqrt(megapixels * 1_000_000))}px on a square`}
                >
                  <Select
                    id={mpId}
                    value={String(megapixels)}
                    onChange={(e) => setMegapixels(Number(e.target.value))}
                  >
                    {[1, 2, 4].map((mp) => (
                      <option key={mp} value={mp} disabled={!allowedMp.includes(mp)}>
                        {mp} MP{allowedMp.includes(mp) ? '' : ' — not available'}
                      </option>
                    ))}
                  </Select>
                </FormField>
              )}
            </div>
          </div>
        </div>

        {/* ── Right: the result, and the library picker that covers it ──── */}
        <div
          className={`relative flex min-h-[26rem] flex-col border-t border-line-soft md:min-h-0 md:flex-1 md:border-t-0 ${
            postContext ? 'md:max-h-[62vh]' : 'md:max-h-[74vh]'
          }`}
        >
          <GenerationPreview
            status={generating ? 'generating' : error ? 'error' : active ? 'done' : 'idle'}
            error={error}
            active={active}
            versions={versions}
            activeIndex={activeIndex}
            onSelectVersion={setActiveIndex}
            specSummary={`${ratioLabel(aspect)} · ${megapixels} MP · ${model?.label ?? '—'}`}
            aspect={aspect}
            changeRequest={changeRequest}
            onChangeRequest={setChangeRequest}
            canRefine={Boolean(model?.caps.canRefine)}
            onUpdate={() => submit({ refine: true })}
            onRegenerate={() => submit({ reseed: true })}
            onViewInLibrary={viewInLibrary}
            onRetry={() => submit()}
          />
          {libraryOpen && (
            <MediaLibraryOverlay
              selected={refs}
              max={maxRefs}
              onChange={setRefs}
              onClose={() => {
                setLibraryOpen(false);
                setLibraryRole(undefined);
              }}
              onUpload={() => setUploadSignal((n) => n + 1)}
              role={libraryRole}
            />
          )}
        </div>
      </div>

      {/*
        Across the whole bottom, right-aligned. Once there is an image, Generate steps back to a
        secondary "Generate new" and the way out becomes the primary action: "Save and return to
        post" from the post editor, which puts the image on screen in the post, or "Save and close"
        anywhere else. Either way the image is already in the Media Library; nothing here is lost
        by closing.
      */}
      <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap justify-end gap-2 border-t border-line-soft bg-card px-5 py-3 md:static">
        <Button variant="secondary" onClick={requestClose} disabled={generating || saveToPost.isPending}>
          Cancel
        </Button>
        <Button
          variant={active ? 'secondary' : 'primary'}
          loading={generating || start.isPending}
          disabled={!canGenerate || saveToPost.isPending}
          aria-describedby={showNotice ? noticeId : undefined}
          onClick={() => submit({ reseed: versions.length > 0 })}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {versions.length > 0 ? 'Generate new' : 'Generate'}
        </Button>
        {active && (
          <Button
            loading={saveToPost.isPending}
            disabled={generating || (onSaveToPost != null && !active.savedAssetId)}
            onClick={() => {
              if (onSaveToPost && active.savedAssetId) saveToPost.mutate(active.savedAssetId);
              else requestClose();
            }}
          >
            {onSaveToPost ? 'Save and return to post' : 'Save and close'}
          </Button>
        )}
      </div>
      </div>
    </Modal>
  );
}
